import { nextFiveAM } from '../StoreManagement/channelSnoozePresets';

export interface ItemSnoozePreset {
  /** Full label — desktop and bulk editor. */
  label: string;
  /** Compact label for mobile chips, where horizontal space is tight. */
  shortLabel: string;
  key: string;
  getValue: () => Date;
}

/**
 * Hard ceiling on how far ahead an item can be snoozed. This is a Byte Graph
 * server-side limit, not ours — it rejects expiries beyond this window. We
 * mirror it client-side so the calendar greys out unreachable dates instead of
 * letting users pick one and hit a server error on submit. Raising it requires
 * a Byte Graph API change.
 */
export const MAX_SNOOZE_DAYS = 60;

export const MAX_SNOOZE_HELPER_TEXT = `Items can be snoozed for up to ${MAX_SNOOZE_DAYS} days.`;

/** The latest expiry Byte Graph will accept, measured from now. */
export function maxSnoozeDate(from: Date = new Date()): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + MAX_SNOOZE_DAYS);
  return d;
}

/** Clamp an expiry so it never lands past the 60-day ceiling. */
function capAtMax(date: Date): Date {
  const ceiling = maxSnoozeDate();
  return date > ceiling ? ceiling : date;
}

const addHours = (hours: number) => () => {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  return capAtMax(d);
};

const addDays = (days: number) => () => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return capAtMax(d);
};

const addMonths = (months: number) => () => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  // A 2-month span runs 59–62 days depending on the month, so the clamp is
  // load-bearing here, not just defensive.
  return capAtMax(d);
};

/**
 * Quick expiry options, shared by the desktop item page, the mobile bottom
 * sheet and the bulk editor so all three offer the same durations.
 */
export const ITEM_SNOOZE_PRESETS: ItemSnoozePreset[] = [
  { label: '1 H', shortLabel: '1H', key: '1h', getValue: addHours(1) },
  { label: '2 H', shortLabel: '2H', key: '2h', getValue: addHours(2) },
  { label: '4 H', shortLabel: '4H', key: '4h', getValue: addHours(4) },
  { label: '6 H', shortLabel: '6H', key: '6h', getValue: addHours(6) },
  { label: '12 H', shortLabel: '12H', key: '12h', getValue: addHours(12) },
  { label: 'End of Day (5 AM)', shortLabel: 'EOD', key: 'endOfDay', getValue: () => nextFiveAM() },
  { label: '24 Hours', shortLabel: '24H', key: 'tomorrow', getValue: addHours(24) },
  { label: '1 Week', shortLabel: '1W', key: '1week', getValue: addDays(7) },
  { label: '1 Month', shortLabel: '1M', key: '1month', getValue: addMonths(1) },
  { label: '2 Months', shortLabel: '2M', key: '2months', getValue: addMonths(2) },
];

/**
 * Preset pre-selected when the desktop / bulk snooze dialog opens. Mobile
 * deliberately starts with NO duration selected — its confirm button used to
 * sit in a pinned footer, so users tapped Snooze before scrolling to the
 * duration and got an accidental end-of-day snooze.
 */
export const DEFAULT_ITEM_SNOOZE_PRESET_KEY = 'endOfDay';

export const DEFAULT_ITEM_SNOOZE_PRESET: ItemSnoozePreset = ITEM_SNOOZE_PRESETS.find(
  (p) => p.key === DEFAULT_ITEM_SNOOZE_PRESET_KEY
)!;
